import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Modal
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Calculate as CalculateIcon,
  MoreVert as MoreVertIcon,
  LockOpen as LockOpenIcon,
  ReceiptLong as ReceiptLongIcon,
  Download as DownloadIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { useAccesos } from '../contexts/AccesosContext';

const TablaCierresDiarios = ({ datos, onVerDetalle, onImprimir, onEditarTransacciones, onReejecutarPartida, onCorrerPartida, onAbrirCierre, server }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('fechaInicio');
  const [order, setOrder] = useState('desc');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [openModalPdf, setOpenModalPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfNombre, setPdfNombre] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const { tienePermiso } = useAccesos();

  useEffect(() => {
    if (!openModalPdf && pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
    }
  }, [openModalPdf]);

  const handleOpenMenu = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Ordenar datos
  const sortedData = useMemo(() => {
    if (!datos || datos.length === 0) return [];

    return [...datos].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Convertir a número si es un campo numérico
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Comparación de strings o fechas
      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [datos, order, orderBy]);

  useEffect(() => {
    setSelectedIds([]);
  }, [datos]);

  // Paginar datos
  const paginatedData = useMemo(() => {
    return sortedData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedData, page, rowsPerPage]);

  const getRowId = (row) => row.id;

  const getEstadoCierre = (row) => {
    if (!esCierreCerrado(row?.fechaCierre)) return 'ABIERTO';
    if (row?.contabilizado === true) return 'CONTABILIZADO';
    if (row?.revisionContable === true) return 'REVISION_ACTIVA';
    return 'CERRADO';
  };

  const getEstadoLabel = (estado) => {
    if (estado === 'ABIERTO') return 'ABIERTO';
    if (estado === 'CERRADO') return 'CERRADO';
    if (estado === 'REVISION_ACTIVA') return 'REVISION ACTIVA';
    if (estado === 'CONTABILIZADO') return 'CONTABILIZADO';
    return estado;
  };

  const selectedRows = useMemo(() => {
    const set = new Set(selectedIds);
    return sortedData.filter((row) => set.has(getRowId(row)));
  }, [sortedData, selectedIds]);

  const selectedEstado = selectedRows.length > 0 ? getEstadoCierre(selectedRows[0]) : null;

  const handleToggleRowSelection = (row) => {
    const rowId = getRowId(row);
    if (!rowId) return;

    const yaSeleccionado = selectedIds.includes(rowId);
    if (yaSeleccionado) {
      setSelectedIds((prev) => prev.filter((id) => id !== rowId));
      return;
    }

    if (selectedRows.length > 0) {
      const estadoSeleccion = getEstadoCierre(selectedRows[0]);
      const estadoFila = getEstadoCierre(row);
      if (estadoSeleccion !== estadoFila) {
        Swal.fire({
          icon: 'info',
          title: 'Seleccion por estado',
          text: `Solo se pueden seleccionar cierres con el mismo estado (${estadoSeleccion})`
        });
        return;
      }
    }

    setSelectedIds((prev) => [...prev, rowId]);
  };

  const seleccionarMismoEstado = () => {
    if (selectedRows.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Seleccione un cierre',
        text: 'Primero marque un registro para identificar el estado a seleccionar'
      });
      return;
    }

    const estadoBase = getEstadoCierre(selectedRows[0]);
    const idsMismoEstado = sortedData
      .filter((row) => getEstadoCierre(row) === estadoBase)
      .map((row) => getRowId(row))
      .filter(Boolean);

    setSelectedIds(idsMismoEstado);
  };

  const limpiarSeleccion = () => setSelectedIds([]);

  const handleActivarRevisionMasivo = async () => {
    if (selectedRows.length === 0) {
      Swal.fire('Validacion', 'Seleccione al menos un cierre', 'warning');
      return;
    }

    if (selectedEstado !== 'CERRADO') {
      Swal.fire('Validacion', 'La accion masiva de revision solo aplica a cierres cerrados', 'warning');
      return;
    }

    if (typeof onReejecutarPartida === 'function') {
      await onReejecutarPartida(selectedRows, { esMasivo: true });
      setSelectedIds([]);
    }
  };

  const handleCorrerPartidaMasivo = async () => {
    if (selectedRows.length === 0) {
      Swal.fire('Validacion', 'Seleccione al menos un cierre', 'warning');
      return;
    }

    if (!['REVISION_ACTIVA', 'CONTABILIZADO'].includes(selectedEstado)) {
      Swal.fire('Validacion', 'La accion masiva de partida solo aplica a cierres en revision activa o contabilizados', 'warning');
      return;
    }

    if (typeof onCorrerPartida === 'function') {
      await onCorrerPartida(selectedRows, { esMasivo: true });
      setSelectedIds([]);
    }
  };

  // Calcular totales
  const totales = useMemo(() => {
    if (!datos || datos.length === 0) return {
      totalContado: 0,
      totalCredito: 0,
      efectivo: 0,
      totalCorte: 0,
      puntosLACM: 0,
      totalTransferencia: 0,
      totalPOS: 0,
      totalACH: 0,
      totalIngresos: 0,
    
    };

    return datos.reduce((acc, row) => {
      if (!row) return acc;
      return {
        totalContado: acc.totalContado + (row.factContado || 0),
        totalCredito: acc.totalCredito + (row.factCredito || 0),
        efectivo: acc.efectivo + (row.efectivo || 0),
        totalCorte: acc.totalCorte + (row.corte || row.Corte || 0),
        puntosLACM: acc.puntosLACM + (row.puntosLACM || 0),
        totalTransferencia: acc.totalTransferencia + (row.totalTransferencia || 0),
        totalPOS: acc.totalPOS + (row.totalPOSValorAcreditado || 0),
        totalACH: acc.totalACH + (row.totalACH || 0),
        totalIngresos: acc.totalIngresos + (row.totalIngresos || 0),
        
      };
    }, {
      totalContado: 0,
      totalCredito: 0,
      efectivo: 0,
      totalCorte: 0,
      puntosLACM: 0,
      totalTransferencia: 0,
      totalPOS: 0,
      totalACH: 0,
      totalIngresos: 0,
      
    });
  }, [datos]);

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return new Intl.NumberFormat('es-HN', {
      style: 'currency',
      currency: 'HNL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (date.getFullYear() <= 1900) return '';
    return date.toLocaleString('es-HN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncarTexto = (texto, maxLength = 30) => {
    if (!texto) return '-';
    if (texto.length <= maxLength) return texto;
    return texto.substring(0, maxLength) + '...';
  };

  const getDiferenciaColor = (diferencia) => {
    if (diferencia === 0) return 'success';
    if (diferencia > 0) return 'warning';
    return 'error';
  };

  const getRowHighlightStyles = (row) => {
    // Verde tenue: partida contable ya corrida
    if (row?.contabilizado === true) {
      return {
        backgroundColor: '#e8f5e9',
        '&:hover': { backgroundColor: '#dcedc8 !important' }
      };
    }

    // Amarillo tenue: cierre en revision contable
    if (row?.revisionContable === true) {
      return {
        backgroundColor: '#fffde7',
        '&:hover': { backgroundColor: '#fff9c4 !important' }
      };
    }

    return {};
  };

  const verPartidaContable = async (idPartida) => {
    if (!idPartida) return;
    try {
      Swal.fire({ title: 'Cargando partida...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const response = await fetch(`${server}/InformesContablesLinux/asiento/${idPartida}/ReportAsiento`, {
        method: 'GET',
        headers: { 'Accept': 'application/pdf' }
      });
      if (!response.ok) throw new Error(`Error al generar el PDF: ${response.status}`);
      const blob = await response.blob();
      const urlBlob = window.URL.createObjectURL(blob);
      const nombreArchivo = `Asiento_${idPartida}_${new Date().toISOString().split('T')[0]}.pdf`;
      setPdfUrl(urlBlob);
      setPdfNombre(nombreArchivo);
      setOpenModalPdf(true);
      Swal.close();
    } catch (err) {
      console.error('Error al ver partida contable:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo generar el PDF de la partida' });
    }
  };

  const handleCloseModalPdf = () => setOpenModalPdf(false);

  const handleDownloadPdf = () => {
    if (pdfUrl && pdfNombre) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = pdfNombre;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  function esCierreCerrado(fechaCierre) {
    if (!fechaCierre) return false;
    // Verificar si es una fecha válida y no es el valor mínimo (01/01/0001)
    const fecha = new Date(fechaCierre);
    return fecha.getFullYear() > 1900;
  }

  const exportarExcel = () => {
    const datosExportar = datos.map(row => ({
      'Colaborador': row.colaborador,
      'Sucursal': row.sucursal,
      'Fecha Inicio': formatDate(row.fechaInicio),
      'Fecha Cierre': formatDate(row.fechaCierre),
      'Factura Contado': row.factContado,
      'Factura Credito': row.factCredito,
      'Efectivo': row.efectivo,
      'Corte': row.corte ?? row.Corte ?? 0,
      'Puntos LACM': row.puntosLACM,
      'Total Transferencia': row.totalTransferencia,
      'Total POS': row.totalPOSValorAcreditado,
      'Total ACH': row.totalACH,
      'Total Ingresos': row.totalIngresos,
      'Revisado': row.revisionContable === true ? 'Si' : 'No',
      'Partida contable': row.contabilizado === true ? 'Si' : 'No',
      'No. Partida': row.idPartidaContable || '',
      'Diferencia': row.diferencia,
      'Supervisor': row.supervisor,
      'Comentarios': row.comentarios
    }));

    // Agregar fila de totales
    datosExportar.push({
      'Colaborador': 'TOTALES',
      'Sucursal': '',
      'Fecha Inicio': '',
      'Fecha Cierre': '',
      'Factura Contado': totales.totalContado,
      'Factura Credito': totales.totalCredito,
      'Efectivo': totales.efectivo,
      'Corte': totales.totalCorte,
      'Puntos LACM': totales.puntosLACM,
      'Total Transferencia': totales.totalTransferencia,
      'Total POS': totales.totalPOS,
      'Total ACH': totales.totalACH,
      'Total Ingresos': totales.totalIngresos,
      'Revisado': '',
      'Partida contable': '',
      'No. Partida': '',
      'Diferencia': '',
      'Supervisor': '',
      'Comentarios': ''
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(datosExportar);

    // Ajustar ancho de columnas
    ws['!cols'] = [
      { wch: 25 }, // Colaborador
      { wch: 20 }, // Sucursal
      { wch: 18 }, // Fecha Inicio
      { wch: 18 }, // Fecha Cierre
      { wch: 20 }, // factura Contado
      { wch: 20 }, // factura Credito
      { wch: 12 }, // Efectivo
      { wch: 12 }, // Corte
      { wch: 12 }, // Puntos LACM
      { wch: 15 }, // Total Transferencia
      { wch: 12 }, // Total POS
      { wch: 12 }, // Total ACH
      { wch: 12 }, // Total Ingresos
      { wch: 12 }, // Revisado
      { wch: 16 }, // Partida contable
      { wch: 14 }, // No. Partida
      { wch: 12 }, // Diferencia
      { wch: 15 }, // Supervisor
      { wch: 40 }  // Comentarios
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Cierres Diarios');
    XLSX.writeFile(wb, `CierresDiarios_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const columnas = [
    { id: 'seleccion', label: '', minWidth: 40, align: 'center' },
    { id: 'acciones', label: 'Acciones', minWidth: 60, align: 'center' },
    { id: 'colaborador', label: 'Colaborador', minWidth: 180 },
    { id: 'sucursal', label: 'Sucursal', minWidth: 150 },
    { id: 'sistema', label: 'Sistema', minWidth: 90 },
    { id: 'fechaInicio', label: 'Fecha Inicio', minWidth: 150 },
    { id: 'fechaCierre', label: 'Fecha Cierre', minWidth: 150 },
    { id: 'factContado', label: 'Factura Contado', minWidth: 120, align: 'right' },
    { id: 'factCredito', label: 'Factura Credito', minWidth: 120, align: 'right' },
    { id: 'efectivo', label: 'Efectivo', minWidth: 110, align: 'right' },
    { id: 'corte', label: 'Corte', minWidth: 110, align: 'right' },
    { id: 'puntosLACM', label: 'Puntos LACM', minWidth: 110, align: 'right' },
    { id: 'totalTransferencia', label: 'Total Transf.', minWidth: 120, align: 'right' },
    { id: 'totalPOSValorAcreditado', label: 'Total POS', minWidth: 110, align: 'right' },
    { id: 'totalACH', label: 'Total ACH', minWidth: 110, align: 'right' },
    { id: 'totalIngresos', label: 'Total Ingresos', minWidth: 120, align: 'right' },
    { id: 'diferencia', label: 'Diferencia', minWidth: 100, align: 'right' },
    { id: 'idPartidaContable', label: 'Partida', minWidth: 90, align: 'center' }
  ];


  return (
    <Box>
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Seleccionados: {selectedRows.length}{selectedEstado ? ` (${getEstadoLabel(selectedEstado)})` : ''}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" size="small" onClick={seleccionarMismoEstado}>
              Seleccionar mismo estado
            </Button>
            <Button
              variant="contained"
              size="small"
              color="info"
              onClick={handleActivarRevisionMasivo}
              disabled={selectedRows.length === 0 || selectedEstado !== 'CERRADO' || !tienePermiso('Activar Revisión Contable')}
            >
              Activar revisión seleccionados
            </Button>
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={handleCorrerPartidaMasivo}
              disabled={selectedRows.length === 0 || !['REVISION_ACTIVA', 'CONTABILIZADO'].includes(selectedEstado) || !tienePermiso('Correr Partida Contable')}
            >
              Correr partida seleccionados
            </Button>
            <Button variant="text" size="small" onClick={limpiarSeleccion} disabled={selectedRows.length === 0}>
              Limpiar seleccion
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Botón de exportar */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<GetAppIcon />}
          onClick={exportarExcel}
          disabled={!tienePermiso('Exportar Excel Cierre')}
        >
          Exportar a Excel
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columnas.map((columna) => (
                <TableCell
                  key={columna.id}
                  align={columna.align || 'left'}
                  style={{ minWidth: columna.minWidth, fontWeight: 'bold' }}
                >
                  {columna.id !== 'acciones' && columna.id !== 'seleccion' ? (
                    <TableSortLabel
                      active={orderBy === columna.id}
                      direction={orderBy === columna.id ? order : 'asc'}
                      onClick={() => handleRequestSort(columna.id)}
                    >
                      {columna.label}
                    </TableSortLabel>
                  ) : (
                    columna.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow hover key={row.id || index} sx={getRowHighlightStyles(row)}>
                <TableCell align="center">
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(getRowId(row))}
                    onChange={() => handleToggleRowSelection(row)}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Acciones">
                    <IconButton size="small" onClick={(e) => handleOpenMenu(e, row)}>
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>{row.colaborador}</TableCell>
                <TableCell>{row.sucursal}</TableCell>
                <TableCell>{row.sistema || '-'}</TableCell>
                <TableCell>{formatDate(row.fechaInicio)}</TableCell>
                <TableCell>{formatDate(row.fechaCierre)}</TableCell>
                <TableCell align="right">{formatNumber(row.factContado)}</TableCell>
                <TableCell align="right">{formatNumber(row.factCredito)}</TableCell>
                <TableCell align="right">{formatNumber(row.efectivo)}</TableCell>
                <TableCell align="right">{formatNumber(row.corte ?? row.Corte)}</TableCell>
                <TableCell align="right">{formatNumber(row.puntosLACM)}</TableCell>
                <TableCell align="right">{formatNumber(row.totalTransferencia)}</TableCell>
                <TableCell align="right">{formatNumber(row.totalPOSValorAcreditado)}</TableCell>
                <TableCell align="right">{formatNumber(row.totalACH)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {formatNumber(row.totalIngresos)}
                </TableCell>
                <TableCell align="right">
                  {esCierreCerrado(row.fechaCierre) ? (
                    <Chip
                      label={formatNumber(row.diferencia)}
                      color={getDiferenciaColor(row.diferencia)}
                      size="small"
                    />
                  ) : (
                    <Chip
                      label="Sin cerrar"
                      color="warning"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell align="center">
                  {row.idPartidaContable ? (
                    <Tooltip title={`Ver Partida #${row.idPartidaContable}`}>
                      <IconButton size="small" color="success" onClick={() => verPartidaContable(row.idPartidaContable)}>
                        <ReceiptLongIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {/* Fila de totales */}
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>
                TOTALES
              </TableCell>
              <TableCell colSpan={4}></TableCell>
              <TableCell colSpan={2}></TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatNumber(totales.totalContado)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatNumber(totales.totalCredito)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatNumber(totales.efectivo)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatNumber(totales.totalCorte)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatNumber(totales.puntosLACM)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatNumber(totales.totalTransferencia)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatNumber(totales.totalPOS)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatNumber(totales.totalACH)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatNumber(totales.totalIngresos)}
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={sortedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />

      {/* Menú de acciones */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuRow?.sistema !== 'Fox' && (
          <MenuItem
            onClick={() => { onVerDetalle(menuRow); handleCloseMenu(); }}
            disabled={!tienePermiso('Ver Detalle Cierre')}
          >
            <ListItemIcon><VisibilityIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>Ver Detalle</ListItemText>
          </MenuItem>
        )}
        {menuRow?.sistema !== 'Fox' && (
          <MenuItem
            onClick={() => { onEditarTransacciones(menuRow); handleCloseMenu(); }}
            disabled={!tienePermiso('Editar Transacción Bancaria')}
          >
            <ListItemIcon><EditIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText>Editar Transacciones</ListItemText>
          </MenuItem>
        )}
        {menuRow?.sistema !== 'Fox' && (
          <MenuItem
            onClick={() => { onImprimir(menuRow); handleCloseMenu(); }}
            disabled={!esCierreCerrado(menuRow?.fechaCierre) || !tienePermiso('Imprimir Cierre Diario')}
          >
            <ListItemIcon><PrintIcon fontSize="small" color="secondary" /></ListItemIcon>
            <ListItemText>Imprimir Documento</ListItemText>
          </MenuItem>
        )}
        <Divider />
        {onReejecutarPartida && (
          <MenuItem
            onClick={() => { onReejecutarPartida(menuRow); handleCloseMenu(); }}
            disabled={!esCierreCerrado(menuRow?.fechaCierre) || menuRow?.revisionContable === true || !tienePermiso('Activar Revisión Contable')}
          >
            <ListItemIcon><PlayArrowIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText>Activar Revisión Contable</ListItemText>
          </MenuItem>
        )}
        {onCorrerPartida && (
          <MenuItem
            onClick={() => { onCorrerPartida(menuRow); handleCloseMenu(); }}
            disabled={!(menuRow?.revisionContable === true || menuRow?.contabilizado === true) || !tienePermiso('Correr Partida Contable')}
          >
            <ListItemIcon><CalculateIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText>Correr Partida Contable</ListItemText>
          </MenuItem>
        )}
        {onAbrirCierre && menuRow?.sistema !== 'Fox' && (
          <MenuItem
            onClick={() => { onAbrirCierre(menuRow); handleCloseMenu(); }}
            disabled={!esCierreCerrado(menuRow?.fechaCierre) || menuRow?.contabilizado === true || !tienePermiso('Abrir Cierre')}
          >
            <ListItemIcon><LockOpenIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Abrir Cierre</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Modal para visualizar PDF de partida contable */}
      <Modal open={openModalPdf} onClose={handleCloseModalPdf}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: '90%', height: '90vh', bgcolor: 'white', boxShadow: 24, borderRadius: 2,
          display: 'flex', flexDirection: 'column', overflow: 'hidden'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #ddd', bgcolor: '#f5f5f5' }}>
            <Typography variant="h6">{pdfNombre}</Typography>
            <Box>
              <IconButton onClick={handleDownloadPdf} color="primary" title="Descargar PDF" sx={{ mr: 1 }}>
                <DownloadIcon />
              </IconButton>
              <IconButton onClick={handleCloseModalPdf} color="error" title="Cerrar">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden', bgcolor: '#525659' }}>
            {pdfUrl && (
              <iframe src={pdfUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="Visor de PDF" />
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderTop: '1px solid #ddd', bgcolor: '#f5f5f5' }}>
            <Button variant="contained" color="error" onClick={handleCloseModalPdf} startIcon={<CloseIcon />}>
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default TablaCierresDiarios;
